import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { TransportGuideDialogHost } from "@/components/warehouse/TransportGuideDialog";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { useDailyTOConlineSync } from "@/hooks/useDailyTOConlineSync";
import { useToconlineSyncSignal } from "@/hooks/useToconlineSyncSignal";
import { ContextMenuGuard } from "@/components/ContextMenuGuard";
import { CartProvider } from "@/hooks/useCart";
import React, { Suspense, lazy, useState, useEffect } from "react";
import { ScrollToTop } from "@/components/ScrollToTop";
import { NoIndexInternal } from "@/components/NoIndexInternal";
import { LanguageRouter } from "@/components/LanguageRouter";
import { ModuleDock } from "@/components/shared/ModuleDock";
import { RoutePrefetcher } from "@/components/RoutePrefetcher";

import { trackPageView } from "@/lib/analytics";
import { syncClarityWithRoute } from "@/lib/clarity";
import { useLocation } from "react-router-dom";
import { isInternalRoute } from "@/lib/internalRoutes";

// Critical path - loaded immediately
import Index from "./pages/Index";
import NotFound from "./pages/NotFound";
import LegacyRedirect from "./components/LegacyRedirect";

// Lazy load non-critical providers for faster FCP
const InactivityProvider = lazy(() => import("@/components/InactivityProvider").then(m => ({ default: m.InactivityProvider })));
const AnalyticsProvider = lazy(() => import("@/hooks/useAnalyticsTracker").then(m => ({ default: m.AnalyticsProvider })));
const WhatsAppChatWidget = lazy(() => import("@/components/WhatsAppChatWidget").then(m => ({ default: m.WhatsAppChatWidget })));
const AIAssistantWidget = lazy(() => import("@/components/ai/AIAssistantWidget").then(m => ({ default: m.AIAssistantWidget })));

const AdminLiveVisitorsWidget = lazy(() => import("@/components/admin/AdminLiveVisitorsWidget").then(m => ({ default: m.AdminLiveVisitorsWidget })));

// Loading fallback component - minimal for faster FCP
const PageLoader = () => (
  <div className="page-loader">
    <div className="page-loader-spinner" />
  </div>
);

// Lazy loaded pages - Public
const Equipment = lazy(() => import("./pages/Equipment"));
const EquipmentCategory = lazy(() => import("./pages/EquipmentCategory"));
const EquipmentDetail = lazy(() => import("./pages/EquipmentDetail"));
const Consumables = lazy(() => import("./pages/Consumables"));
const ConsumablesCategory = lazy(() => import("./pages/ConsumablesCategory"));
const ConsumableDetail = lazy(() => import("./pages/ConsumableDetail"));
const Shop = lazy(() => import("./pages/Shop"));
const Cart = lazy(() => import("./pages/Cart"));
const Checkout = lazy(() => import("./pages/Checkout"));
const Auth = lazy(() => import("./pages/Auth"));
const OAuthConsent = lazy(() => import("./pages/OAuthConsent"));
const AgentConnect = lazy(() => import("./pages/AgentConnect"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const Account = lazy(() => import("./pages/Account"));
const TechnicalSupport = lazy(() => import("./pages/TechnicalSupport"));
const ScheduleAssistance = lazy(() => import("./pages/ScheduleAssistance"));
const Contact = lazy(() => import("./pages/Contact"));
const About = lazy(() => import("./pages/About"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogArticle = lazy(() => import("./pages/BlogArticle"));
const Solutions = lazy(() => import("./pages/Solutions"));
const CheckoutSuccess = lazy(() => import("./pages/CheckoutSuccess"));
const CheckoutCanceled = lazy(() => import("./pages/CheckoutCanceled"));
const OrderTracking = lazy(() => import("./pages/OrderTracking"));
const Financing = lazy(() => import("./pages/Financing"));
const DTF = lazy(() => import("./pages/DTF"));
const ImpressorasUV = lazy(() => import("./pages/ImpressorasUV"));
const ImpressoraSublimacao = lazy(() => import("./pages/ImpressoraSublimacao"));
const PrensaTermica = lazy(() => import("./pages/PrensaTermica"));
const PlotterCorte = lazy(() => import("./pages/PlotterCorte"));
const Calandras = lazy(() => import("./pages/Calandras"));
const LaserCO2 = lazy(() => import("./pages/LaserCO2"));
const EstamparTShirtsDTF = lazy(() => import("./pages/EstamparTShirtsDTF"));
const ReparacaoCabecaI3200 = lazy(() => import("./pages/ReparacaoCabecaI3200"));
const InstallApp = lazy(() => import("./pages/InstallApp"));
const TermsConditions = lazy(() => import("./pages/TermsConditions"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));

// Lazy loaded pages - Admin
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const AdminEquipment = lazy(() => import("./pages/admin/AdminEquipment"));
const AdminConsumables = lazy(() => import("./pages/admin/AdminConsumables"));
const CatalogHub = lazy(() => import("./pages/catalog/CatalogHub"));
const AdminOrders = lazy(() => import("./pages/admin/AdminOrders"));
const AdminCustomers = lazy(() => import("./pages/admin/AdminCustomers"));
const AdminClientView = lazy(() => import("./pages/admin/AdminClientView"));
const AdminClientViewDetail = lazy(() => import("./pages/admin/AdminClientViewDetail"));
const AdminCommercialView = lazy(() => import("./pages/admin/AdminCommercialView"));
const AdminCommercialViewDetail = lazy(() => import("./pages/admin/AdminCommercialViewDetail"));
const AdminAppointments = lazy(() => import("./pages/admin/AdminAppointments"));
const AdminMessages = lazy(() => import("./pages/admin/AdminMessages"));
const AdminPricing = lazy(() => import("./pages/admin/AdminPricing"));
const AdminPricingAI = lazy(() => import("./pages/admin/AdminPricingAI"));
const AdminCompatibility = lazy(() => import("./pages/admin/AdminCompatibility"));
const AdminPricingRequests = lazy(() => import("./pages/admin/AdminPricingRequests"));
const AdminBanners = lazy(() => import("./pages/admin/AdminBanners"));
const AdminBlog = lazy(() => import("./pages/admin/AdminBlog"));
const AdminSettings = lazy(() => import("./pages/admin/AdminSettings"));
const AdminCategories = lazy(() => import("./pages/admin/AdminCategories"));
const AdminNewsletter = lazy(() => import("./pages/admin/AdminNewsletter"));
const AdminCredits = lazy(() => import("./pages/admin/AdminCredits"));
const AdminCreditTerms = lazy(() => import("./pages/admin/AdminCreditTerms"));
const AdminPayments = lazy(() => import("./pages/admin/AdminPayments"));
const AdminNotifications = lazy(() => import("./pages/admin/AdminNotifications"));
const AdminTranslations = lazy(() => import("./pages/admin/AdminTranslations"));
const AdminUsers = lazy(() => import("./pages/admin/AdminUsers"));
const AdminNavigation = lazy(() => import("./pages/admin/AdminNavigation"));
const AdminFooterSettings = lazy(() => import("./pages/admin/AdminFooterSettings"));
const AdminContactSettings = lazy(() => import("./pages/admin/AdminContactSettings"));
const WebsiteAnalyzer = lazy(() => import("./pages/admin/WebsiteAnalyzer"));
const AdminSEO = lazy(() => import("./pages/admin/AdminSEO"));
const AdminSEORouteAudit = lazy(() => import("./pages/admin/AdminSEORouteAudit"));
const AdminSEORouteAuditHistory = lazy(() => import("./pages/admin/AdminSEORouteAuditHistory"));
const AdminPageContent = lazy(() => import("./pages/admin/AdminPageContent"));
const AdminLegalPages = lazy(() => import("./pages/admin/AdminLegalPages"));
const AdminInstallments = lazy(() => import("./pages/admin/AdminInstallments"));
const AdminAnalytics = lazy(() => import("./pages/admin/AdminAnalytics"));
const CRMAnalytics = lazy(() => import("./pages/crm/CRMAnalytics"));
const CRMAudiences = lazy(() => import("./pages/crm/CRMAudiences"));
const AdminShipping = lazy(() => import("./pages/admin/AdminShipping"));
const AdminSystemStatus = lazy(() => import("./pages/admin/AdminSystemStatus"));
const AdminCommercials = lazy(() => import("./pages/admin/AdminCommercials"));
const AdminCommercialDashboard = lazy(() => import("./pages/admin/AdminCommercialDashboard"));
const AdminPopupNotifications = lazy(() => import("./pages/admin/AdminPopupNotifications"));
const AdminPreventiveMaintenanceMetrics = lazy(() => import("./pages/admin/AdminPreventiveMaintenanceMetrics"));
const AdminPreventiveFunnel = lazy(() => import("./pages/admin/AdminPreventiveFunnel"));
const AdminInventory = lazy(() => import("./pages/admin/AdminInventory"));
const AdminConsumptions = lazy(() => import("./pages/admin/AdminConsumptions"));
const AdminStockMovements = lazy(() => import("./pages/admin/AdminStockMovements"));

// Lazy loaded pages - Warehouse
const WarehouseDashboard = lazy(() => import("./pages/warehouse/WarehouseDashboard"));
const WarehouseInventory = lazy(() => import("./pages/warehouse/WarehouseInventory"));
const WarehouseReceiving = lazy(() => import("./pages/warehouse/WarehouseReceiving"));
const WarehouseLocations = lazy(() => import("./pages/warehouse/WarehouseLocations"));

// Lazy loaded pages - Mobile warehouse app (/app)
const HexaVoice = lazy(() => import("./pages/HexaVoice"));
const AppShell = lazy(() => import("./pages/app/AppShell"));
const AppHome = lazy(() => import("./pages/app/AppHome"));
const AppStock = lazy(() => import("./pages/app/AppStock"));
const AppProductDetail = lazy(() => import("./pages/app/AppProductDetail"));
const AppPicking = lazy(() => import("./pages/app/AppPicking"));
const AppPickingDetail = lazy(() => import("./pages/app/AppPickingDetail"));
const AppCounts = lazy(() => import("./pages/app/AppCounts"));
const AppCountDetail = lazy(() => import("./pages/app/AppCountDetail"));



// Lazy loaded pages - Commercial
const CommercialDashboard = lazy(() => import("./pages/commercial/CommercialDashboard"));
 
// Lazy loaded pages - CRM
const CRMDashboard = lazy(() => import("./pages/crm/CRMDashboard"));
const CRMClients = lazy(() => import("./pages/crm/CRMClients"));
const CRMClientDetail = lazy(() => import("./pages/crm/CRMClientDetail"));
const CRMProposals = lazy(() => import("./pages/crm/CRMProposals"));
const CRMProformaInvoices = lazy(() => import("./pages/crm/CRMProformaInvoices"));
const CRMTasks = lazy(() => import("./pages/crm/CRMTasks"));
const CRMMeetings = lazy(() => import("./pages/crm/CRMMeetings"));
const CRMReminders = lazy(() => import("./pages/crm/CRMReminders"));
const CRMSettings = lazy(() => import("./pages/crm/CRMSettings"));
const CRMAutomations = lazy(() => import("./pages/crm/CRMAutomations"));
const CRMAgenda = lazy(() => import("./pages/crm/CRMAgenda"));
const CRMEquipmentDashboard = lazy(() => import("./pages/crm/CRMEquipmentDashboard"));
const CRMVisitsDashboard = lazy(() => import("./pages/crm/CRMVisitsDashboard"));

// Lazy loaded pages - Technical Service
const ServiceLayout = lazy(() => import("./pages/technical-service/ServiceLayout"));
const ServiceDashboard = lazy(() => import("./pages/technical-service/ServiceDashboard"));
const ServiceCompanies = lazy(() => import("./pages/technical-service/ServiceCompanies"));
const ServiceCompanyEquipment = lazy(() => import("./pages/technical-service/ServiceCompanyEquipment"));
const ServiceContracts = lazy(() => import("./pages/technical-service/ServiceContracts"));
const ServiceEquipment = lazy(() => import("./pages/technical-service/ServiceEquipment"));
const ServiceEquipmentHistory = lazy(() => import("./pages/technical-service/ServiceEquipmentHistory"));
const ServiceTickets = lazy(() => import("./pages/technical-service/ServiceTickets"));
const ServiceTicketDetail = lazy(() => import("./pages/technical-service/ServiceTicketDetail"));
const ServiceParts = lazy(() => import("./pages/technical-service/ServiceParts"));
const ServiceAlerts = lazy(() => import("./pages/technical-service/ServiceAlerts"));
const ServiceChecklists = lazy(() => import("./pages/technical-service/ServiceChecklists"));
const ScheduledMaintenances = lazy(() => import("./pages/technical-service/ScheduledMaintenances"));
const MaintenanceExecution = lazy(() => import("./pages/technical-service/MaintenanceExecution"));
const ExternalTechnicians = lazy(() => import("./pages/technical-service/ExternalTechnicians"));
const ServiceSettings = lazy(() => import("./pages/technical-service/ServiceSettings"));
const MaintenanceProposalAccepted = lazy(() => import("./pages/MaintenanceProposalAccepted"));
const MaintenanceProposalView = lazy(() => import("./pages/MaintenanceProposalView"));
const ServiceProposalAccepted = lazy(() => import("./pages/ServiceProposalAccepted"));

// Lazy loaded pages - Accounting
const AccountingLayout = lazy(() => import("./pages/accounting/AccountingLayout"));
const PaymentReconciliation = lazy(() => import("./pages/accounting/PaymentReconciliation"));
const InvoiceDuplicateAudit = lazy(() => import("./pages/accounting/InvoiceDuplicateAudit"));
const AccountingDashboard = lazy(() => import("./pages/accounting/AccountingDashboard"));
const AccountingExpenses = lazy(() => import("./pages/accounting/AccountingExpenses"));
const AccountingSales = lazy(() => import("./pages/accounting/AccountingSales"));
const AccountingReports = lazy(() => import("./pages/accounting/AccountingReports"));
const AccountingInvoicing = lazy(() => import("./pages/accounting/AccountingInvoicing"));
const AccountingReconciliation = lazy(() => import("./pages/accounting/AccountingReconciliation"));
const ToconlineReconciliation = lazy(() => import("./pages/accounting/ToconlineReconciliation"));
const AccountingPurchases = lazy(() => import("./pages/accounting/AccountingPurchases"));
const AccountingSuppliers = lazy(() => import("./pages/accounting/AccountingSuppliers"));
const EmailImportConfig = lazy(() => import("./pages/accounting/EmailImportConfig"));
const GmailInvoiceSearch = lazy(() => import("./pages/accounting/GmailInvoiceSearch"));
const BankStatements = lazy(() => import("./pages/accounting/BankStatements"));
const BankStatementDetail = lazy(() => import("./pages/accounting/BankStatementDetail"));
const BankReconciliationWorkbench = lazy(() => import("./pages/accounting/BankReconciliationWorkbench"));
const AccountingSalaries = lazy(() => import("./pages/accounting/AccountingSalaries"));
const ExecutiveDashboard = lazy(() => import("./pages/accounting/ExecutiveDashboard"));
const ExecutiveAIPage = lazy(() => import("./pages/accounting/ExecutiveAIPage"));
const ExecutiveIntelligencePage = lazy(() => import("./pages/accounting/ExecutiveIntelligencePage"));

// Lazy loaded pages - Bureaucracy
const BureaucracyLayout = lazy(() => import("./pages/bureaucracy/BureaucracyLayout"));
const BureaucracyDashboard = lazy(() => import("./pages/bureaucracy/BureaucracyDashboard"));
const BureaucracyContracts = lazy(() => import("./pages/bureaucracy/BureaucracyContracts"));
const BureaucracyEmployees = lazy(() => import("./pages/bureaucracy/BureaucracyEmployees"));
const BureaucracyVehicles = lazy(() => import("./pages/bureaucracy/BureaucracyVehicles"));
const BureaucracyInsurance = lazy(() => import("./pages/bureaucracy/BureaucracyInsurance"));
const BureaucracyCredits = lazy(() => import("./pages/bureaucracy/BureaucracyCredits"));
const BureaucracyRentals = lazy(() => import("./pages/bureaucracy/BureaucracyRentals"));
const BureaucracyLegalDocs = lazy(() => import("./pages/bureaucracy/BureaucracyLegalDocs"));
const BureaucracyLicenses = lazy(() => import("./pages/bureaucracy/BureaucracyLicenses"));
const BureaucracyAlerts = lazy(() => import("./pages/bureaucracy/BureaucracyAlerts"));

// Lazy loaded pages - Procurement
const ProcurementLayout = lazy(() => import("./pages/procurement/ProcurementLayout"));
const ProcurementDashboard = lazy(() => import("./pages/procurement/ProcurementDashboard"));
const ProcurementShipments = lazy(() => import("./pages/procurement/ProcurementShipments"));
const ProcurementSuppliers = lazy(() => import("./pages/procurement/ProcurementSuppliers"));
const ProcurementProducts = lazy(() => import("./pages/procurement/ProcurementProducts"));
const ProcurementContainer = lazy(() => import("./pages/procurement/ProcurementContainer"));
const ProcurementInsights = lazy(() => import("./pages/procurement/ProcurementInsights"));
const ProcurementContainerPlans = lazy(() => import("./pages/procurement/ProcurementContainerPlans"));
const ProcurementScenarios = lazy(() => import("./pages/procurement/ProcurementScenarios"));
const ProcurementPurchasePlan = lazy(() => import("./pages/procurement/ProcurementPurchasePlan"));
const ProcurementImports = lazy(() => import("./pages/procurement/ProcurementImports"));
const ContainerDetail = lazy(() => import("./pages/procurement/ContainerDetail"));
const AIWorkspace = lazy(() => import("./pages/ia/AIWorkspace"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

// Admin PT/BR switch: refetch everything so no cached rows from the other
// market stay on screen (query keys don't all include the market).
if (typeof window !== "undefined") {
  let lastAdminMarket: string | null = null;
  window.addEventListener("hx-admin-market-change", (e) => {
    const m = (e as CustomEvent<string>).detail ?? null;
    if (lastAdminMarket !== null && m !== lastAdminMarket) queryClient.invalidateQueries();
    lastAdminMarket = m;
  });
}

// Public routes configuration (for language prefix support)
const publicRoutes = [
  { path: "", element: <Index /> },
  { path: "equipamentos", element: <Equipment /> },
  { path: "equipment", element: <Equipment /> },
  { path: "equipos", element: <Equipment /> },
  { path: "sprzet", element: <Equipment /> },
  { path: "equipements", element: <Equipment /> },
  { path: "equipamentos/:category", element: <EquipmentCategory /> },
  { path: "equipment/:category", element: <EquipmentCategory /> },
  { path: "equipos/:category", element: <EquipmentCategory /> },
  { path: "sprzet/:category", element: <EquipmentCategory /> },
  { path: "equipements/:category", element: <EquipmentCategory /> },
  { path: "equipamento/:slug", element: <EquipmentDetail /> },
  { path: "equipment-detail/:slug", element: <EquipmentDetail /> },
  { path: "consumiveis", element: <Consumables /> },
  { path: "consumables", element: <Consumables /> },
  { path: "consumibles", element: <Consumables /> },
  { path: "materialy-eksploatacyjne", element: <Consumables /> },
  { path: "consommables", element: <Consumables /> },
  { path: "consumiveis/:category", element: <ConsumablesCategory /> },
  { path: "consumables/:category", element: <ConsumablesCategory /> },
  { path: "consumibles/:category", element: <ConsumablesCategory /> },
  { path: "materialy-eksploatacyjne/:category", element: <ConsumablesCategory /> },
  { path: "consommables/:category", element: <ConsumablesCategory /> },
  { path: "consumivel/:slug", element: <ConsumableDetail /> },
  { path: "consumable/:slug", element: <ConsumableDetail /> },
  { path: "loja", element: <Shop /> },
  { path: "shop", element: <Shop /> },
  { path: "tienda", element: <Shop /> },
  { path: "sklep", element: <Shop /> },
  { path: "boutique", element: <Shop /> },
  { path: "carrinho", element: <Cart /> },
  { path: "cart", element: <Cart /> },
  { path: "carrito", element: <Cart /> },
  { path: "koszyk", element: <Cart /> },
  { path: "panier", element: <Cart /> },
  { path: "checkout", element: <Checkout /> },
  { path: "zamowienie", element: <Checkout /> },
  { path: "commander", element: <Checkout /> },
  { path: "checkout/success", element: <CheckoutSuccess /> },
  { path: "checkout/canceled", element: <CheckoutCanceled /> },
  { path: "auth", element: <Auth /> },
  { path: "reset-password", element: <ResetPassword /> },
  { path: ".lovable/oauth/consent", element: <OAuthConsent /> },
  { path: "connect", element: <AgentConnect /> },
  { path: "recuperar-password", element: <ResetPassword /> },
  { path: "conta", element: <Account /> },
  { path: "account", element: <Account /> },
  { path: "cuenta", element: <Account /> },
  { path: "konto", element: <Account /> },
  { path: "compte", element: <Account /> },
  { path: "encomenda/:orderNumber", element: <OrderTracking /> },
  { path: "order/:orderNumber", element: <OrderTracking /> },
  { path: "pedido/:orderNumber", element: <OrderTracking /> },
  { path: "commande/:orderNumber", element: <OrderTracking /> },
  { path: "assistencia", element: <TechnicalSupport /> },
  { path: "support", element: <TechnicalSupport /> },
  { path: "soporte", element: <TechnicalSupport /> },
  { path: "wsparcie", element: <TechnicalSupport /> },
  { path: "assistance", element: <TechnicalSupport /> },
  { path: "agendar", element: <ScheduleAssistance /> },
  { path: "agendar-assistencia", element: <ScheduleAssistance /> },
  { path: "schedule", element: <ScheduleAssistance /> },
  { path: "schedule-assistance", element: <ScheduleAssistance /> },
  { path: "agendar-asistencia", element: <ScheduleAssistance /> },
  { path: "umow-wizyte", element: <ScheduleAssistance /> },
  { path: "planifier", element: <ScheduleAssistance /> },
  { path: "contacto", element: <Contact /> },
  { path: "contact", element: <Contact /> },
  { path: "kontakt", element: <Contact /> },
  { path: "sobre", element: <About /> },
  { path: "about", element: <About /> },
  { path: "nosotros", element: <About /> },
  { path: "o-nas", element: <About /> },
  { path: "a-propos", element: <About /> },
  { path: "blog", element: <Blog /> },
  { path: "blog/:slug", element: <BlogArticle /> },
  { path: "solucoes", element: <Solutions /> },
  { path: "solutions", element: <Solutions /> },
  { path: "soluciones", element: <Solutions /> },
  { path: "rozwiazania", element: <Solutions /> },
  { path: "financiamento", element: <Financing /> },
  { path: "financing", element: <Financing /> },
  { path: "financiacion", element: <Financing /> },
  { path: "finansowanie", element: <Financing /> },
  { path: "financement", element: <Financing /> },
  { path: "dtf", element: <DTF /> },
  { path: "impressoras-uv", element: <ImpressorasUV /> },
  { path: "impressora-uv", element: <ImpressorasUV /> },
  { path: "impressora-sublimacao", element: <ImpressoraSublimacao /> },
  { path: "sublimacao", element: <ImpressoraSublimacao /> },
  { path: "prensa-termica", element: <PrensaTermica /> },
  { path: "prensas-termicas", element: <PrensaTermica /> },
  { path: "plotter-corte", element: <PlotterCorte /> },
  { path: "plotter-de-corte", element: <PlotterCorte /> },
  { path: "calandras", element: <Calandras /> },
  { path: "calandra", element: <Calandras /> },
  { path: "laser-co2", element: <LaserCO2 /> },
  { path: "maquina-laser-co2", element: <LaserCO2 /> },
  { path: "estampar-t-shirts-dtf", element: <EstamparTShirtsDTF /> },
  { path: "reparacao-cabeca-epson-i3200", element: <ReparacaoCabecaI3200 /> },
  { path: "reparacao-cabeca-i3200", element: <ReparacaoCabecaI3200 /> },
  { path: "como-estampar-t-shirts", element: <EstamparTShirtsDTF /> },
  { path: "personalizar-t-shirts", element: <EstamparTShirtsDTF /> },
  { path: "instalar", element: <InstallApp /> },
  { path: "install", element: <InstallApp /> },
  { path: "instaluj", element: <InstallApp /> },
  { path: "installer", element: <InstallApp /> },
  { path: "termos-condicoes", element: <TermsConditions /> },
  { path: "terms-conditions", element: <TermsConditions /> },
  { path: "terminos-condiciones", element: <TermsConditions /> },
  { path: "regulamin", element: <TermsConditions /> },
  { path: "conditions-generales", element: <TermsConditions /> },
  { path: "politica-privacidade", element: <PrivacyPolicy /> },
  { path: "privacy-policy", element: <PrivacyPolicy /> },
  { path: "politica-privacidad", element: <PrivacyPolicy /> },
  { path: "politique-confidentialite", element: <PrivacyPolicy /> },
  { path: "polityka-prywatnosci", element: <PrivacyPolicy /> },
  { path: "termos", element: <Navigate to="/termos-condicoes" replace /> },
  { path: "privacidade", element: <Navigate to="/politica-privacidade" replace /> },
  { path: "privacy", element: <Navigate to="/politica-privacidade" replace /> },
];

// Wrapper component for deferred providers.
// IMPORTANT: always return the same JSX root shape, or React will unmount
// and remount the entire subtree — which resets scroll, form state and
// double-fires page-view tracking on first paint.
const DeferredProviders = ({ children }: { children: React.ReactNode }) => {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    if ('requestIdleCallback' in window) {
      const id = window.requestIdleCallback(() => setMounted(true), { timeout: 1500 });
      return () => window.cancelIdleCallback(id);
    } else {
      const id = setTimeout(() => setMounted(true), 500);
      return () => clearTimeout(id);
    }
  }, []);

  return (
    <Suspense fallback={children}>
      <InactivityProvider>
        <AnalyticsProvider>
          {children}
          {mounted && <WhatsAppChatWidget />}
          {mounted && <AdminLiveVisitorsWidget />}
        </AnalyticsProvider>
      </InactivityProvider>
    </Suspense>
  );
};

// Assistente IA flutuante — apenas nas áreas internas (exceto a própria página /ia)
const InternalAIAssistant = () => {
  const location = useLocation();
  if (!isInternalRoute(location.pathname)) return null;
  if (location.pathname === "/ia" || location.pathname.startsWith("/ia/")) return null;
  if (location.pathname.startsWith("/hexa")) return null;
  return (
    <Suspense fallback={null}>
      <AIAssistantWidget />
    </Suspense>
  );
};

// Google Analytics route tracker (skips internal/back-office routes)
const GARouteTracker = () => {
  const location = useLocation();
  useEffect(() => {
    if (isInternalRoute(location.pathname)) return;
    trackPageView(location.pathname + location.search);
  }, [location]);
  return null;
};

// Microsoft Clarity route tracker — only records registered customers
// (logged in, non-admin) on public/shop routes, identified by name/company.
const ClarityRouteTracker = () => {
  const location = useLocation();
  const { user, profile, isAdmin, loading } = useAuth();
  const [companyName, setCompanyName] = useState<string | null>(null);
  const companyId = profile?.company_id ?? null;

  useEffect(() => {
    let cancelled = false;
    setCompanyName(null);
    if (!companyId) return;
    supabase.from("companies").select("name").eq("id", companyId).maybeSingle()
      .then(({ data }) => { if (!cancelled) setCompanyName(data?.name ?? null); });
    return () => { cancelled = true; };
  }, [companyId]);

  useEffect(() => {
    if (loading) return;
    const internal = isInternalRoute(location.pathname);
    if (!user || isAdmin) {
      syncClarityWithRoute(internal, null);
      return;
    }
    const clientName = [profile?.first_name, profile?.last_name].filter(Boolean).join(" ") || user.email || null;
    const friendlyName = [companyName, clientName].filter(Boolean).join(" · ") || user.id;
    syncClarityWithRoute(internal, { userId: user.id, friendlyName, clientName, companyName });
  }, [location, user, profile, isAdmin, loading, companyName]);
  return null;
};

// Daily TOConline sync (best-effort; cron at 06:00/13:00 UTC is source of truth)
const DailySyncTrigger = () => {
  useDailyTOConlineSync();
  useToconlineSyncSignal();
  return null;
};

const App = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <DailySyncTrigger />
        <ContextMenuGuard />
        <CartProvider>
          <TooltipProvider>
            <Toaster />
            <Sonner />
            <TransportGuideDialogHost />
            <BrowserRouter>
              <LanguageRouter>
                <DeferredProviders>
                  <ScrollToTop />
                  <NoIndexInternal />
                  <GARouteTracker />
                  <ClarityRouteTracker />
                  <ModuleDock />
                  <InternalAIAssistant />
                  <RoutePrefetcher />
                  <Suspense fallback={<PageLoader />}>

                    <Routes>
                      {/* Portuguese routes (default, no prefix) */}
                      {publicRoutes.map((route) => (
                        <Route key={route.path} path={`/${route.path}`} element={route.element} />
                      ))}
                      
                      {/* English routes */}
                      {publicRoutes.map((route) => (
                        <Route key={`en-${route.path}`} path={`/en/${route.path}`} element={route.element} />
                      ))}
                      
                      {/* Spanish routes */}
                      {publicRoutes.map((route) => (
                        <Route key={`es-${route.path}`} path={`/es/${route.path}`} element={route.element} />
                      ))}
                      
                      {/* Polish routes */}
                      {publicRoutes.map((route) => (
                        <Route key={`pl-${route.path}`} path={`/pl/${route.path}`} element={route.element} />
                      ))}
                      
                      {/* French routes */}
                      {publicRoutes.map((route) => (
                        <Route key={`fr-${route.path}`} path={`/fr/${route.path}`} element={route.element} />
                      ))}
                      
                      {/* Admin Routes (no language prefix) */}
                      <Route path="/admin" element={<AdminDashboard />} />
                      <Route path="/catalogo" element={<CatalogHub />} />
                      <Route path="/admin/produtos" element={<Navigate to="/catalogo" replace />} />
                      <Route path="/admin/equipamentos" element={<Navigate to="/catalogo?tab=equipamentos" replace />} />
                      <Route path="/admin/consumiveis" element={<Navigate to="/catalogo?tab=consumiveis" replace />} />
                      <Route path="/admin/encomendas" element={<AdminOrders />} />
                      <Route path="/admin/clientes" element={<AdminCustomers />} />
                      <Route path="/admin/vista-cliente" element={<AdminClientView />} />
                      <Route path="/admin/vista-cliente/:userId" element={<AdminClientViewDetail />} />
                      <Route path="/admin/vista-comercial" element={<AdminCommercialView />} />
                      <Route path="/admin/vista-comercial/:userId" element={<AdminCommercialViewDetail />} />
                      <Route path="/admin/agendamentos" element={<AdminAppointments />} />
                      <Route path="/admin/mensagens" element={<AdminMessages />} />
                      <Route path="/admin/precos" element={<AdminPricing />} />
                      <Route path="/admin/pricing-ai" element={<Navigate to="/catalogo?tab=pricing-ai" replace />} />
                      <Route path="/catalogo/pricing-ai" element={<Navigate to="/catalogo?tab=pricing-ai" replace />} />
                      <Route path="/admin/pedidos-preco" element={<AdminPricingRequests />} />
                      <Route path="/admin/banners" element={<AdminBanners />} />
                      <Route path="/admin/compatibilidade" element={<Navigate to="/catalogo?tab=compatibilidade" replace />} />
                      <Route path="/admin/blog" element={<AdminBlog />} />
                      <Route path="/admin/configuracoes" element={<AdminSettings />} />
                      <Route path="/admin/categorias" element={<Navigate to="/catalogo?tab=categorias" replace />} />
                      <Route path="/admin/newsletter" element={<AdminNewsletter />} />
                      <Route path="/admin/creditos" element={<AdminCredits />} />
                      <Route path="/admin/condicoes-credito" element={<AdminCreditTerms />} />
                      <Route path="/admin/pagamentos" element={<AdminPayments />} />
                      <Route path="/admin/notificacoes" element={<AdminNotifications />} />
                      <Route path="/admin/traducoes" element={<AdminTranslations />} />
                      <Route path="/admin/utilizadores" element={<AdminUsers />} />
                      <Route path="/admin/navegacao" element={<AdminNavigation />} />
                      <Route path="/admin/rodape" element={<AdminFooterSettings />} />
                      <Route path="/admin/contacto" element={<AdminContactSettings />} />
                      <Route path="/admin/paginas" element={<AdminPageContent />} />
                      <Route path="/admin/conteudo-legal" element={<AdminLegalPages />} />
                      <Route path="/admin/financiamento" element={<AdminInstallments />} />
                      <Route path="/admin/analytics" element={<Navigate to="/crm/analytics" replace />} />
                      <Route path="/admin/portes" element={<AdminShipping />} />
                      <Route path="/admin/website-analyzer" element={<WebsiteAnalyzer />} />
                      <Route path="/admin/seo" element={<AdminSEO />} />
                      <Route path="/admin/seo/route-audit" element={<AdminSEORouteAudit />} />
                      <Route path="/admin/seo/route-audit/history" element={<AdminSEORouteAuditHistory />} />
                      <Route path="/admin/status" element={<AdminSystemStatus />} />
                      <Route path="/admin/comerciais" element={<AdminCommercials />} />
                      <Route path="/admin/comerciais/:commercialId" element={<AdminCommercialDashboard />} />
                      <Route path="/admin/popup-notificacoes" element={<AdminPopupNotifications />} />
                      <Route path="/admin/manutencao-preventiva" element={<AdminPreventiveMaintenanceMetrics />} />
                      <Route path="/admin/manutencao-preventiva/funil" element={<AdminPreventiveFunnel />} />
                      <Route path="/admin/inventario" element={<Navigate to="/catalogo?tab=inventario" replace />} />
                      <Route path="/admin/consumos" element={<AdminConsumptions />} />
                      <Route path="/admin/movimentos-stock" element={<Navigate to="/catalogo?tab=movimentos" replace />} />
                      
                      {/* Warehouse Routes */}
                      <Route path="/armazem" element={<WarehouseDashboard />} />
                      <Route path="/armazem/inventario" element={<WarehouseInventory />} />
                      <Route path="/armazem/recepcao" element={<WarehouseReceiving />} />
                      <Route path="/armazem/localizacoes" element={<WarehouseLocations />} />

                      <Route path="/hexa" element={<HexaVoice />} />
                      {/* Mobile warehouse app */}
                      <Route path="/app" element={<AppShell />}>
                        <Route index element={<AppHome />} />
                        <Route path="stock" element={<AppStock />} />
                        <Route path="stock/:productType/:id" element={<AppProductDetail />} />
                        <Route path="picking" element={<AppPicking />} />
                        <Route path="picking/:id" element={<AppPickingDetail />} />
                        <Route path="contagens" element={<AppCounts />} />
                        <Route path="contagens/:id" element={<AppCountDetail />} />
                      </Route>

                      
                      {/* Commercial Routes */}
                      <Route path="/comercial" element={<CommercialDashboard />} />
                       
                      {/* CRM Routes */}
                      <Route path="/crm" element={<CRMDashboard />} />
                      <Route path="/crm/atividades" element={<CRMVisitsDashboard />} />
                      <Route path="/crm/visitas" element={<CRMVisitsDashboard />} />
                      <Route path="/crm/clientes" element={<CRMClients />} />
                      <Route path="/crm/cliente/:id" element={<CRMClientDetail />} />
                      <Route path="/crm/clientes/:id" element={<CRMClientDetail />} />
                      <Route path="/crm/equipamentos" element={<CRMEquipmentDashboard />} />
                      <Route path="/crm/manutencoes" element={<ScheduledMaintenances />} />
                      <Route path="/crm/manutencao/:id" element={<MaintenanceExecution />} />
                      <Route path="/crm/propostas" element={<CRMProposals />} />
                      <Route path="/crm/faturas-proforma" element={<CRMProformaInvoices />} />
                      <Route path="/crm/tarefas" element={<CRMTasks />} />
                      <Route path="/crm/reunioes" element={<CRMVisitsDashboard />} />
                      <Route path="/crm/lembretes" element={<CRMTasks />} />
                      <Route path="/crm/configuracoes" element={<CRMSettings />} />
                      <Route path="/crm/automacoes" element={<CRMAutomations />} />
                      <Route path="/crm/agenda" element={<CRMDashboard />} />
                      <Route path="/crm/analytics" element={<CRMAnalytics />} />
                      <Route path="/crm/audiencias" element={<CRMAudiences />} />
                      
                      {/* Technical Service Routes */}
                      <Route path="/assistencia-tecnica" element={<ServiceLayout />}>
                        <Route index element={<ServiceDashboard />} />
                        <Route path="assistencias" element={<ServiceTickets />} />
                        <Route path="assistencias/novo" element={<ServiceTickets />} />
                        <Route path="assistencias/:id" element={<ServiceTicketDetail />} />
                        <Route path="empresas" element={<ServiceCompanies />} />
                        <Route path="empresas/:companyId/equipamentos" element={<ServiceCompanyEquipment />} />
                        <Route path="contratos" element={<ServiceContracts />} />
                        <Route path="equipamentos" element={<ServiceEquipment />} />
                        <Route path="equipamentos/:id/historico" element={<ServiceEquipmentHistory />} />
                        <Route path="pecas" element={<ServiceParts />} />
                        <Route path="alertas" element={<ServiceAlerts />} />
                        <Route path="checklists" element={<ServiceChecklists />} />
                        <Route path="manutencoes" element={<ScheduledMaintenances />} />
                        <Route path="manutencao/:id" element={<MaintenanceExecution />} />
                      <Route path="intervencoes" element={<ServiceTickets />} />
                      <Route path="tecnicos-externos" element={<ExternalTechnicians />} />
                      <Route path="configuracoes" element={<ServiceSettings />} />
                      </Route>
                      
                      {/* Accounting Routes */}
                      <Route path="/contabilidade" element={<AccountingLayout />}>
                        <Route index element={<AccountingDashboard />} />
                        <Route path="vendas" element={<AccountingSales />} />
                        <Route path="despesas" element={<AccountingExpenses />} />
                        <Route path="faturacao" element={<AccountingInvoicing />} />
                        <Route path="conciliacao" element={<AccountingReconciliation />} />
                        <Route path="compras" element={<AccountingPurchases />} />
                        <Route path="fornecedores" element={<AccountingSuppliers />} />
                        <Route path="relatorios" element={<AccountingReports />} />
                        <Route path="reconciliacao-toconline" element={<ToconlineReconciliation />} />
                        <Route path="reconciliacao-pagamentos" element={<PaymentReconciliation />} />
                        <Route path="duplicados" element={<InvoiceDuplicateAudit />} />
                        <Route path="extratos" element={<BankStatements />} />
                        <Route path="extratos/:id" element={<BankStatementDetail />} />
                        <Route path="reconciliacao" element={<BankReconciliationWorkbench />} />
                        <Route path="email-config" element={<EmailImportConfig />} />
                        <Route path="pesquisa-email" element={<GmailInvoiceSearch />} />
                        <Route path="salarios" element={<AccountingSalaries />} />
                        <Route path="executive" element={<ExecutiveDashboard />} />
                        <Route path="executive/ai" element={<ExecutiveAIPage />} />
                        <Route path="executive/intelligence" element={<ExecutiveIntelligencePage />} />
                      </Route>
                      
                      {/* Bureaucracy Routes */}
                      <Route path="/burocracia" element={<BureaucracyLayout />}>
                        <Route index element={<BureaucracyDashboard />} />
                        <Route path="contratos" element={<BureaucracyContracts />} />
                        <Route path="funcionarios" element={<BureaucracyEmployees />} />
                        <Route path="veiculos" element={<BureaucracyVehicles />} />
                        <Route path="seguros" element={<BureaucracyInsurance />} />
                        <Route path="creditos" element={<BureaucracyCredits />} />
                        <Route path="imoveis" element={<BureaucracyRentals />} />
                        <Route path="documentos-legais" element={<BureaucracyLegalDocs />} />
                        <Route path="licencas" element={<BureaucracyLicenses />} />
                        <Route path="alertas" element={<BureaucracyAlerts />} />
                      </Route>

                      {/* Procurement Routes */}
                      <Route path="/procurement" element={<ProcurementLayout />}>
                        <Route index element={<ProcurementDashboard />} />
                        <Route path="shipments" element={<ProcurementShipments />} />
                        <Route path="suppliers" element={<ProcurementSuppliers />} />
                        <Route path="products" element={<ProcurementProducts />} />
                        <Route path="container" element={<ProcurementContainer />} />
                        <Route path="container-plans" element={<ProcurementContainerPlans />} />
                        <Route path="scenarios" element={<ProcurementScenarios />} />
                        <Route path="purchase-plan" element={<ProcurementPurchasePlan />} />
                        <Route path="imports" element={<ProcurementImports />} />
                        <Route path="imports/:id" element={<ContainerDetail />} />
                        <Route path="insights" element={<ProcurementInsights />} />
                      </Route>

                      {/* AI Workspace */}
                      <Route path="/ia" element={<AIWorkspace />} />


                      {/* Public Maintenance Proposal Acceptance */}
                      <Route path="/aceitar-manutencao/:token" element={<MaintenanceProposalAccepted />} />
                      <Route path="/proposta-manutencao/:token" element={<MaintenanceProposalView />} />
                      <Route path="/aceitar-proposta-servico/:token" element={<ServiceProposalAccepted />} />
                      
                      {/* Legacy WordPress/WooCommerce redirects for SEO */}
                      <Route path="/dtf-distruibuidor-official-portugal" element={<LegacyRedirect to="/dtf" />} />
                      <Route path="/dtf-distruibuidor-official-portugal/*" element={<LegacyRedirect to="/dtf" />} />
                      <Route path="/impressora-dtf-hexa-6023" element={<LegacyRedirect to="/equipamento/hexa-6023-dtf" />} />
                      <Route path="/impressora-dtf-hexa-6023/*" element={<LegacyRedirect to="/equipamento/hexa-6023-dtf" />} />
                      <Route path="/produto/*" element={<LegacyRedirect to="/equipamentos" />} />
                      <Route path="/product/*" element={<LegacyRedirect to="/equipamentos" />} />
                      <Route path="/product-category/*" element={<LegacyRedirect to="/equipamentos" />} />
                      <Route path="/shop" element={<LegacyRedirect to="/equipamentos" />} />
                      <Route path="/shop/*" element={<LegacyRedirect to="/equipamentos" />} />
                      <Route path="/loja" element={<LegacyRedirect to="/equipamentos" />} />
                      <Route path="/loja/*" element={<LegacyRedirect to="/equipamentos" />} />
                      
                      {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                      <Route path="*" element={<NotFound />} />
                    </Routes>
                  </Suspense>
                </DeferredProviders>
              </LanguageRouter>
            </BrowserRouter>
          </TooltipProvider>
        </CartProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
};

export default App;
